import { UseFormRegister } from 'react-hook-form'
import clsx from 'clsx'
import TextFormField from '@/components/forms/text-form-field'
import { Text } from '@/components'
import { AccountFieldKey, AccountFieldsConfig } from '@/types/utilityProgram'
import { extractedHelperText } from './utilityAccountFields.helpers'

export type UtilityAccountFieldsProps = {
  /** Resolved config — the caller handles the legacy fallback / empty-skip. */
  config: AccountFieldsConfig
  /** A react-hook-form register, or a Formik-faked one of the same shape. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: UseFormRegister<any>
  /** Current per-field string values. */
  values: Partial<Record<AccountFieldKey, string>>
  /** Per-field error text (only surfaced when the field is also touched). */
  errors?: Partial<Record<AccountFieldKey, string | undefined>>
  touched?: Partial<Record<AccountFieldKey, boolean | undefined>>
  /** Values autofilled from a bill upload → renders "Extracted from bill: X". */
  extractedValues?: Partial<Record<AccountFieldKey, string>>
  placeholders?: Partial<Record<AccountFieldKey, string>>
  className?: string
}

/**
 * Renders the dynamic set of utility-account fields (account / contract / meter
 * number) described by `config`. Presentational and form-library-agnostic so it
 * can be reused by both the enrollment flow (Formik) and the profile page
 * (react-hook-form).
 */
export default function UtilityAccountFields({
  config,
  register,
  values,
  errors,
  touched,
  extractedValues,
  placeholders,
  className,
}: UtilityAccountFieldsProps) {
  return (
    <div className={clsx('flex flex-col gap-5', className)}>
      {config.map((field) => {
        const key = field.field
        const hasError = Boolean(touched?.[key] && errors?.[key])
        const error = hasError ? errors?.[key] : undefined
        const extracted = extractedValues?.[key]
        const label = field.required ? (
          field.label
        ) : (
          <>
            {field.label}
            <span className="font-normal text-themed-grey-400 ml-1">
              (Optional)
            </span>
          </>
        )
        const placeholder =
          placeholders?.[key] ?? `Enter ${field.label.toLowerCase()}`

        return (
          <div key={key} className="w-full">
            <TextFormField
              name={key}
              label={label}
              type="text"
              required={field.required}
              placeholder={placeholder}
              register={register}
              value={values[key] ?? ''}
              error={error}
              showErrorDropdown={hasError}
            />
            {extracted && (
              <Text
                variant="body3"
                className="text-themed-grey-500 pt-2 font-medium"
              >
                {extractedHelperText(extracted)}
              </Text>
            )}
          </div>
        )
      })}
    </div>
  )
}
